import React from "react";
import { Plus, Upload, FileText, User, Globe, TrendingUp, LayoutTemplate } from "lucide-react";
import PageHeader, { PrimaryButton, SecondaryButton, PageKPIItem } from "components/ui/PageHeader";

/* ══════════════════════════════════════════════════════════════════════════
   Templates page header.

   Two changes:

   • The four colour props per KPI are gone; each metric now declares a tone.
   • The metric row is switched on. It was fully computed by the caller and
     then commented out, which left the counts crammed into a run-on subtitle
     ("48 total · 12 yours · 36 global") while every other list page showed
     the same information as scannable tiles. Same data, same source, now
     legible and consistent with Campaigns, Leads and Audiences.
   ══════════════════════════════════════════════════════════════════════════ */

interface TemplatesHeaderProps {
    totalTemplates: number;
    userTemplatesCount: number;
    globalTemplatesCount: number;
    averageUsage: number;
    onCreateTemplate: () => void;
    onImportTemplate?: () => void;
    isLoading?: boolean;
}

const TemplatesHeader: React.FC<TemplatesHeaderProps> = ({
    totalTemplates,
    userTemplatesCount,
    globalTemplatesCount,
    averageUsage,
    onCreateTemplate,
    onImportTemplate,
    isLoading = false,
}) => {
    const kpis: PageKPIItem[] = [
        { icon: FileText, value: totalTemplates, label: "Total Templates", tone: "brand" },
        { icon: User, value: userTemplatesCount, label: "Your Templates", tone: "success" },
        { icon: Globe, value: globalTemplatesCount, label: "Global Templates", tone: "violet" },
        { icon: TrendingUp, value: averageUsage, label: "Avg. Usage", tone: "warning" },
    ];

    return (
        <PageHeader
            icon={<LayoutTemplate className="w-5 h-5" />}
            tone="brand"
            title="Templates"
            subtitle="Reusable designs you can drop into any campaign"
            actions={
                <>
                    {onImportTemplate && (
                        <SecondaryButton icon={<Upload className="w-4 h-4" />} onClick={onImportTemplate}>
                            Import
                        </SecondaryButton>
                    )}
                    <PrimaryButton
                        id="create-template-btn"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={onCreateTemplate}
                    >
                        Create Template
                    </PrimaryButton>
                </>
            }
            kpis={kpis}
            isLoading={isLoading}
        />
    );
};

export default TemplatesHeader;
